import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/api/client";
import { Upload, FileText, TrendingUp, Package, MapPin, Calendar } from "lucide-react";
import { createPageUrl } from "@/utils";
import { useAuth } from "@/lib/AuthContext";

export default function HomePage() {
  const { user: currentUser } = useAuth();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mailingDate, setMailingDate] = useState(new Date().toISOString().split('T')[0]);
  const [mailingName, setMailingName] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [pieceWeight, setPieceWeight] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [stats, setStats] = useState({
    totalRoutes: 0,
    totalMailpieces: 0,
    totalOrders: 0,
    last30Routes: 0,
    last30Mailpieces: 0,
    last30Orders: 0
  });
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      const response = await api.stats();
      if (response) {
        setStats({
          totalRoutes: response.totalRoutes || 0,
          totalMailpieces: response.totalMailpieces || 0,
          totalOrders: response.totalOrders || 0,
          last30Routes: response.last30Routes || 0,
          last30Mailpieces: response.last30Mailpieces || 0,
          last30Orders: response.last30Orders || 0
        });
      }
    } catch (err) {
      console.error('Failed to load statistics', err);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.name) {
      if (!selectedFile.name.toLowerCase().endsWith('.pdf')) {
        setError("Please select a PDF file");
        return;
      }
      setFile(selectedFile);
      setError("");
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.name) {
      if (!droppedFile.name.toLowerCase().endsWith('.pdf')) {
        setError("Please drop a PDF file");
        return;
      }
      setFile(droppedFile);
      setError("");
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError("Please select a file first");
      return;
    }

    if (!mailingName.trim()) {
      setError("Please enter a Mailing Name");
      return;
    }

    if (!orderNumber.trim()) {
      setError("Please enter an Order Number");
      return;
    }

    if (!pieceWeight.trim() || parseFloat(pieceWeight) <= 0) {
      setError("Please enter a valid Piece Weight");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // One server call. It uploads the form, reads it, reconciles the routes
      // against the total the form itself declares, and resolves each ZIP
      // against the verified post office table.
      //
      // A USPS-generated 3602 is a real PDF whose figures are already text, so
      // "reads it" is literal - the values are lifted out of the file. Only a
      // scan needs transcribing, and that path is the fallback.
      //
      // The Base44 version did this in the browser across two model calls, and
      // the second one asked a model to recall a retail post office's street
      // address — which then got copied forward into every future mailing that
      // shared the ZIP.
      const proposal = await api.extraction.parse3602(file);

      navigate(createPageUrl('Review'), {
        state: {
          proposal,
          mailingDate,
          mailingName,
          orderNumber,
          pieceWeight
        }
      });
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to parse PDF. Please ensure it contains a valid PS Form 3602.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <header className="header">
        <div>
            <div className="header-title">Dashboard</div>
            {currentUser &&
            <div className="header-subtitle" style={{ textTransform: 'capitalize' }}>Welcome, {currentUser.full_name || currentUser.email}!</div>
            }
          </div>
      </header>

      {/* Statistics Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "1.5rem", marginBottom: "2rem" }}>
        <div className="card" style={{ padding: "1.5rem", background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)", color: "white" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
            <div>
              <div style={{ fontSize: "0.875rem", opacity: 0.9 }}>Total Routes Processed</div>
              <div style={{ fontSize: "2.5rem", fontWeight: 700 }}>{stats.totalRoutes.toLocaleString()}</div>
              <div style={{ fontSize: "0.75rem", opacity: 0.8, marginTop: "0.5rem" }}>Last 30 days: {stats.last30Routes}</div>
            </div>
            <MapPin size={48} style={{ opacity: 0.3 }} />
          </div>
        </div>

        <div className="card" style={{ padding: "1.5rem", background: "linear-gradient(135deg, #10b981 0%, #059669 100%)", color: "white" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
            <div>
              <div style={{ fontSize: "0.875rem", opacity: 0.9 }}>Total Mailpieces</div>
              <div style={{ fontSize: "2.5rem", fontWeight: 700 }}>{stats.totalMailpieces.toLocaleString()}</div>
              <div style={{ fontSize: "0.75rem", opacity: 0.8, marginTop: "0.5rem" }}>Last 30 days: {stats.last30Mailpieces.toLocaleString()}</div>
            </div>
            <Package size={48} style={{ opacity: 0.3 }} />
          </div>
        </div>

        <div className="card" style={{ padding: "1.5rem", background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)", color: "white" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
            <div>
              <div style={{ fontSize: "0.875rem", opacity: 0.9 }}>Total Orders</div>
              <div style={{ fontSize: "2.5rem", fontWeight: 700 }}>{stats.totalOrders.toLocaleString()}</div>
              <div style={{ fontSize: "0.75rem", opacity: 0.8, marginTop: "0.5rem" }}>Last 30 days: {stats.last30Orders}</div>
            </div>
            <TrendingUp size={48} style={{ opacity: 0.3 }} />
          </div>
        </div>
      </div>

      {/* Upload Section */}
      <div
        className={`card ${isDragging ? 'dragging' : ''}`}
        style={{
          padding: "2rem 3rem",
          textAlign: "center",
          border: "2px dashed #cbd5e1",
          cursor: "pointer",
          transition: "all 0.3s ease"
        }}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}>

        <div
          style={{
            width: "80px",
            height: "80px",
            margin: "0 auto 1.5rem",
            background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
            borderRadius: "20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white"
          }}>

          <Upload size={40} />
        </div>
        <h3 style={{ fontSize: "1.5rem", fontWeight: 600, color: "#1e293b", marginBottom: "0.5rem" }}>
          {file ? file.name : "Upload PS Form 3602"}
        </h3>
        <p style={{ fontSize: "1rem", color: "#64748b", marginBottom: "2rem" }}>
          Drag and drop or click to browse
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          onChange={handleFileSelect}
          style={{ display: "none" }} />

      </div>

      {/* Mailing Details Form */}
      {file &&
      <div className="form-section">
          <div className="form-title">Mailing Details</div>
          {error && <div className="error">{error}</div>}
          
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label" htmlFor="mailing-date">Mailing Date</label>
              <input
              type="date"
              className="form-input"
              id="mailing-date"
              value={mailingDate}
              onChange={(e) => setMailingDate(e.target.value)} />

            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="mailing-name">Mailing Name *</label>
              <input
              type="text"
              className="form-input"
              id="mailing-name"
              value={mailingName}
              onChange={(e) => setMailingName(e.target.value)}
              placeholder="e.g., Spring Campaign 2024" />

            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="order-number">Order Number *</label>
              <input
              type="text"
              className="form-input"
              id="order-number"
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)}
              placeholder="e.g., ORD-12345" />

            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="piece-weight">Piece Weight (oz) *</label>
              <input
              type="number"
              step="0.01"
              className="form-input"
              id="piece-weight"
              value={pieceWeight}
              onChange={(e) => setPieceWeight(e.target.value)}
              placeholder="0.00" />

            </div>
          </div>

          <div style={{ marginTop: "1.5rem", textAlign: "right" }}>
            <button
            className="btn btn-primary"
            onClick={handleUpload}
            disabled={loading}>

              {loading ? "Processing..." : "Continue to Review"}
            </button>
          </div>
        </div>
      }
    </div>
  );
}